import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { useDispatch } from 'react-redux'
import { user } from 'ampersand-app'
import lang from '@/lang'
import { updateDocument } from '@/redux/ducks/document/drawer'
import { Element, Link, animateScroll as scroll, scrollSpy } from 'react-scroll'
import { useCollaborationContext } from '@/modules/document/_helpers/CollaborationContext'
import { Document, Page, Thumbnail } from 'react-pdf'

import PerfectScrollbar from 'react-perfect-scrollbar'
import ThemeInterface from '@/theme-interface'
import IconZoomMinus from '@assets/images/icons/zoom_minus.svg'
import IconZoomPlus from '@assets/images/icons/zoom_plus.svg'
import IconRotate from '@assets/images/icons/reload.svg'
import IconReset from '@assets/images/icons/drag.svg'
import IconDownload from '@assets/images/icons/download.svg'
import IconArrowUp from '@assets/images/icons/arrow_up.svg'
// FLAG:useDocuAi — valid/invalid e-invoice hint icons (mirrors receipt-analyse-result.tsx).
import ValidIcon from '@assets/images/icons/check_circle.svg'
import InvalidIcon from '@assets/images/icons/v2/error-full.svg'

import { DocumentControlButton } from '@/modules/base/components/buttons'
import { times } from 'lodash'
import clsx from 'clsx'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import customDecodeUri from '@/utils/customDecodeUri'
import { mimeTypes, experimentalFeatures } from '@/modules/base/util/constants'
import Badge from '@/modules/base/components/badge/badge'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import getStateInfo from '@/modules/invoice/helpers/getStateInfo'
import DocumentDrawerPreviewImage from '@/modules/document/components/document-drawer/document-drawer-preview-image'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import DocumentDrawerPreviewXml from './document-drawer-preview-xml'
// FLAG:useDocuAi — video/audio player ported from the DMS admindashboard's MediaViewer.
import DocumentDrawerPreviewMedia from './document-drawer-preview-media'
// FLAG:useDocuAi — renders the KoSIT validation report artifact as embedded HTML.
import DocumentDrawerPreviewHtml from './document-drawer-preview-html'
import Pager from './document-drawer-thumbnail-pager'
import { useAppSelector } from '@/redux/store'
import FileService from '@/modules/file/fileService'
import Loader from '@/modules/base/components/loader/loader'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { mergePdfDocuments } from '../../utils/pdfMerger'
// FLAG:useDocuAi — shorten the file name without losing its extension.
import { splitFileNameForDisplay } from '@/modules/document/utils/file-name-display'
// FLAG:useDocuAi — the one rule for "this document gets the media player" (see media-preview.ts).
import { isPlayableMediaPreview } from '@/modules/document/utils/media-preview'
import { loadDocumentPreviewData } from '@/modules/document/utils/document-preview-data'

const ZOOM_STEP = 0.1
const ZOOM_MAX = 2
const ZOOM_MIN = 0.5
const ZOOM_DEFAULT_PDF = 2
const ZOOM_DEFAULT_IMAGE = 1

// FLAG:useDocuAi — how many pages/thumbnails around the current one are actually rasterised.
// Rendering a PDF page is dominated by decoding its images, not by the canvas size: a scanned
// page carrying a 33 MP JPEG2000 costs ~7s to decode, and pdf.js evicts it from its image cache
// as soon as the next page is decoded. Rendering every page AND every thumbnail up front (the
// flag-OFF behaviour) therefore pays that price once per page and once per thumbnail — 6 decodes
// for a 3-page scan — while the user is only ever looking at one page.
// The window starts at 0 (render strictly what is on screen) and only widens once the first page
// has proven itself cheap to rasterise. Pessimistic-first matters: with a fixed window of 1 the
// neighbouring page is already decoding by the time we could measure that the document is slow.
const PAGE_WINDOW_FAST = 1
const THUMBNAIL_WINDOW_FAST = 3
// A page that needs longer than this to rasterise is image-decode bound (scanned JPEG2000 pages
// measured ~7000 ms, ordinary invoices 130-250 ms) — such a document never widens its window.
const SLOW_PAGE_RENDER_MS = 1200
// Fallback until the first page reports its real size (A4 at ZOOM_DEFAULT_PDF).
const PAGE_PLACEHOLDER_HEIGHT = 1650

const {
  MIMETYPE_DOC,
  MIMETYPE_DOCX,
  MIMETYPE_XLS,
  MIMETYPE_XLSX,
  MIMETYPE_PPT,
  MIMETYPE_PPTX,
} = mimeTypes

const thumbnailMimeTypes = [
  MIMETYPE_DOC,
  MIMETYPE_DOCX,
  MIMETYPE_XLS,
  MIMETYPE_XLSX,
  MIMETYPE_PPT,
  MIMETYPE_PPTX,
]

/**
 * True when the drawer's `default:` branch can render the file ITSELF — i.e. the bytes are
 * the preview. Only images qualify: PDF and XML have their own branches above and never
 * reach it.
 *
 * Everything else (archives, .tar/.7z/.zip, CAD, opaque binary, text, …) has no viewer, so
 * the raw bytes were pushed through `rotateAndScaleImage`, which silently produced nothing —
 * an EMPTY preview pane, even when the DMS had a perfectly good thumbnail for the file
 * (archives get an "Archiv-Vorschau" thumbnail and show it on the tile). The office
 * allowlist above used to be the only way to reach the thumbnail path; this widens it to
 * "no viewer ⇒ show the thumbnail if there is one", which is what the office types already
 * did and what the tile does everywhere.
 */
const isRenderableWithoutThumbnail = (mimetype?: string): boolean =>
  (mimetype || '').toLowerCase().startsWith('image/')

type PreviewStyleProps = Partial<{
  isPdf: boolean
  scrollable: boolean
  isCarouselSlide: boolean
  isPlayableMedia: boolean
  titleInsetRight: number
}>

const useStyles = makeStyles<ThemeInterface, PreviewStyleProps>((theme: ThemeInterface) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: ({ isPdf = true }) => (isPdf ? '276px 1fr' : '1fr 13fr'),
    gridTemplateRows: 'max-content',
    gridTemplateAreas: '"title title" "leftArea rightArea"',
    height: '100%',
    width: '100%',
    '@media (max-width: 1700px)': {
      gridTemplateColumns: '93px 1fr !important',
    },
  },
  document: {
    maxHeight: '100%',
    height: '100%',
    overflow: 'auto',
    marginTop: 89,
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '-ms-overflow-style': 'none', // IE and Edge
    'scrollbar-width': 'none',
    '& .react-pdf__Page__canvas': {
      height: '100% !important',
      width: '100% !important',
    },
  },
  image: {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftArea: {
    gridArea: 'leftArea',
    display: 'grid',
    gridTemplateAreas: '"actions thumbnails"',
    gridTemplateColumns: 'min-content max-content',
    overflow: 'hidden',
    height: '100%',
    minWidth: 'fit-content',
  },
  actions: {
    gridArea: 'actions',
    display: 'flex',
    flexDirection: 'column',
    padding: '37px 15px',
    backgroundColor: theme.backgroundColor,
    borderRight: `1px solid ${theme.borderColors.default}`,
  },
  thumbnails: {
    gridArea: 'thumbnails',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    marginTop: 44,
    marginLeft: 11,
    '& a': {
      textDecoration: 'none',
    },
    '& a.active $thumbnail': {
      borderColor: theme.borderColors.outline,
    },
    '@media (max-width: 1700px)': {
      display: 'none',
    },
  },
  thumbnailWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
    padding: '10px 30px',
  },
  thumbnail: {
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    border: '1px solid transparent',
    position: 'relative',
  },
  thumbnailLabel: {
    fontSize: theme.fontSizes.smaller,
    marginTop: 12,
    color: `${theme.colors.black} !important`,
  },
  // FLAG:useDocuAi — stand-ins that keep the scroll height/geometry of a not-yet-rasterised
  // page or thumbnail, so windowing does not make the scrollbar jump.
  pagePlaceholder: {
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    border: `1px solid ${theme.borderColors.default}`,
    marginBottom: 40,
    backgroundColor: theme.colors.white,
  },
  thumbnailPlaceholder: {
    height: 195,
    width: 122,
    backgroundColor: theme.colors.white,
  },
  rightArea: {
    gridArea: 'rightArea',
    display: 'flex',
    justifyContent: 'center',
    alignItems: ({ scrollable = false }) => scrollable ? 'flex-start' : 'center',
    margin: ({ scrollable = false }) => scrollable ? 0 : CssHelper.px(0, 30),
    overflow: ({ scrollable = false }) => scrollable ? 'auto' : 'hidden',
    // FLAG:useDocuAi — `scrollable` is simply "not a PDF", so the media pane inherits
    // overflow:auto on BOTH axes. A video sized to the pane never needs to scroll sideways,
    // and leaving the axis scrollable means any transient 1px overflow turns into a pane the
    // user can drag horizontally. Vertical scrolling is left alone.
    overflowX: ({ isPlayableMedia = false }) => isPlayableMedia ? 'hidden' : null,
    position: 'relative',
    height: '100%',
  },
  page: {
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    border: `1px solid ${theme.borderColors.default}`,
    marginBottom: 40,
  },
  backToTopButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    zIndex: 1000,
  },
  title: {
    fontSize: theme.fontSizes.large,
    height: 87,
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    padding: '0 23px',
    // FLAG:useDocuAi — a host that draws something OVER the right end of this row (the artifact
    // switcher in invoice-artifact-preview) reserves its width here, so the row's own content ends
    // before it instead of running underneath it. Without the reservation the 340px dropdown covered
    // the status badge completely and cut the file name off mid-word — measured at every realistic
    // preview width (520-900px), because the row is 100% wide while the dropdown is right-anchored.
    // Absent prop ⇒ 23px, i.e. the unchanged `padding` above.
    paddingRight: ({ titleInsetRight = 0 }: PreviewStyleProps) => 23 + titleInsetRight,
    backgroundColor: theme.backgroundColor,
    borderBottom: `1px solid ${theme.borderColors.default}`,
    gridArea: 'title',
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '& h1': {
      fontSize: theme.fontSizes.large,
      maxWidth: 500,
      fontWeight: 400,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '& button': {
      margin: 0,
    },
  },
  // FLAG:useDocuAi — an overlong file name is truncated, but its EXTENSION remains visible. Plain
  // `text-overflow: ellipsis` on the full name removed exactly the extension first
  // („Herkunftsland_Zolltarif-Nummer__HS___UNSPSC__…"), and during review .xml versus .pdf is not
  // decoration: it says whether the reviewer is looking at the e-invoice itself or its rendition.
  titleNameWithExtension: {
    display: 'flex',
    alignItems: 'baseline',
    minWidth: 0,
  },
  titleNameStem: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  titleNameExtension: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  previewHeader: {
    display: 'flex',
    marginRight: 'auto',
    '& > *': {
      margin: '10px 0px 0px 10px',
    },
    '& > :first-child': {
      margin: '10px 0px 0px 0px',
    },
  },
  stateField: {
    margin: CssHelper.px(20, 0),
    // FLAG:useDocuAi — the file name yields, the status does not: the name is the one item in this
    // row that can be arbitrarily long, and it already ellipsises (titleNameStem). Without this the
    // badge is a shrinkable flex item too, so a long name could squeeze it instead of itself.
    flexShrink: 0,
  },
  badge: {
    borderRadius: 25,
    fontSize: 11,
  },
  // FLAG:useDocuAi — valid/invalid e-invoice hint, mirrors receipt-analyse-result.tsx's styling.
  validityHint: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginLeft: 16,
    fontSize: theme.fontSizes.smaller,
    fontWeight: theme.fontWeights.medium,
    // Same reason as stateField: the valid/invalid e-invoice state is a verdict, not decoration — it
    // must not be the thing that gives way to a long file name.
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  // Carousel-specific styles
  carouselRoot: {
    height: '100vh',
    position: 'relative',
  },
  carouselDocument: {
    height: 'calc(100vh - 176px)', // Account for title and controls
    maxHeight: 'calc(100vh - 176px)',
    overflow: 'auto',
    marginTop: 0,
  },
  carouselImage: {
    height: 'calc(100vh - 176px)',
    overflow: 'auto',
  },
  carouselLeftArea: {
    height: 'calc(100vh - 87px)',
  },
  carouselThumbnails: {
    height: 'calc(100vh - 200px)',
    overflow: 'hidden',
  },
  carouselScrollbar: {
    height: 'calc(100vh - 240px)',
    maxHeight: 'calc(100vh - 240px)',
  },
  carouselRightArea: {
    height: 'calc(100vh - 87px)', // Account for title height
  },
}), { name: 'DocumentPreview' })

interface DocumentDrawerPreviewProps {
  document?: Record<string, unknown>
  isCarouselSlide?: boolean
  signatureDocument?: {
    url?: string
    minioId?: string
    decodedName?: string
  }
  showSignature?: boolean
  onToggleSignature?: (value: boolean) => void
  // FLAG:useDocuAi — host (review page) shows validity itself → suppress the badge here.
  hideValidityBadge?: boolean
  // FLAG:useDocuAi — px a host draws over the RIGHT end of the title row (see the `title` rule).
  // Omitted ⇒ 0, i.e. the title row is unchanged.
  titleInsetRight?: number
  /**
   * FLAG:useDocuAi — controls the HOST owns, drawn at the BOTTOM of the left tool column, below
   * the download button. That is the verbatim place the legacy preview item keeps them in
   * (`receipt-previewer-item.tsx`: `renderUnlinkOrDelete` closes its toolbar) — the expense /
   * revenue edit preview passes „Dokument entfernen" / „Verknüpfung lösen" here, so the button
   * does not float alone over the document once a file switches to the artifact path.
   * Omitted ⇒ nothing is drawn and the column is unchanged.
   */
  toolbarActions?: React.ReactNode
}

const StateFields = ({ shouldRender = false, document }) => {
  if (!document) return null

  const classes = useStyles({})
  const { label, color } = getStateInfo(document.state, document.isOverDue, document.outstandingAmount)

  return shouldRender && (
    <div className={classes.stateField}>
      <div className={classes.previewHeader}>
        {document?.isAssigned && <Badge label={label} color={color} customClass={classes.badge} />}
      </div>
    </div>
  )
}

const DocumentDrawerPreview: React.FC<DocumentDrawerPreviewProps> = (props) => {
  const dispatch = useDispatch()
  const theme = useTheme<ThemeInterface>()
  const { collaborationId, collaborationHeaders } = useCollaborationContext()

  // The selector is called UNCONDITIONALLY. Behind `||` it was a Rules-of-Hooks violation — the
  // hook order changes the moment a host starts or stops passing `props.document` — and the
  // short-circuit is also the reason the zoom/rotate buttons were dead in every prop-fed host:
  // they dispatch into this very slice, which such a host never reads. See `zoom`/`rotate` below.
  const drawerDocument = useAppSelector((state) => state.document.drawer.document)
  const document = props.document || drawerDocument
  const isCarouselSlide = props.isCarouselSlide || false
  // FLAG:useDocuAi — suppress the validity badge when the host already shows it (the review
  // page renders "E-Rechnung gültig" in its form panel; the drawer keeps it since it has no panel).
  const hideValidityBadge = props.hideValidityBadge || false
  const toolbarActions = props.toolbarActions
  const signatureDocument = props.signatureDocument
  const showSignature = props.showSignature ?? false
  const dmsThumbnailUrl = useAppSelector((state) => state.tenant.settings.dmsThumbnailUrl)
  const dmsFileUrl = useAppSelector((state) => state.tenant.settings.dmsFileUrl)

  const [pageCount, setPageCount] = useState<number>(1)
  const [pdf, setPdf] = useState(null)
  const [data, setData] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [error, setError] = useState(false)
  // FLAG:useDocuAi — real on-screen height of a rasterised page, used to size the placeholders
  // of the pages that windowing skips. Only ever set on the flag-ON path.
  const [pageHeight, setPageHeight] = useState<number | null>(null)
  // FLAG:useDocuAi — 0 until the first page proves the document is cheap to rasterise.
  const [pageWindow, setPageWindow] = useState(0)
  const renderStartedAtRef = useRef<number | null>(null)

  // Signature merging state
  const [mergedPdfData, setMergedPdfData] = useState<string | null>(null)
  const [isMerging, setIsMerging] = useState(false)

  const mimetype = document?.mimetype
  // FLAG:useDocuAi — selects rendering of application/xml DMS artifacts (see getMimeTypeComponent).
  const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const isPdf = mimetype === mimeTypes.MIMETYPE_PDF
  // FLAG:useDocuAi — the same name as in the title below, merely split up into “may be shortened”
  // and “must remain intact”. With flag-OFF, only the h1 below renders, reading the name itself.
  const displayFileName = (document?.decodedName ?? customDecodeUri(document?.name)) as string
  const displayFileNameParts = useMemo(() => splitFileNameForDisplay(displayFileName), [displayFileName])
  const isSignatureFile = document?.tags?.some(tag => tag.label === lang.tags.SIGNATURE)
  const scrollable = mimetype !== mimeTypes.MIMETYPE_PDF

  // Which branch getMimeTypeComponent will take. Derived HERE, once, so `showThumbnail` cannot
  // drift from the actual rendering decision — it also drives the zoom/rotate toolbar, and an
  // independent copy of this logic would silently hide those buttons for a type that does get
  // a real viewer (e.g. XML). Computed BEFORE useStyles: `isPlayableMedia` is a style prop too.
  const effectiveMimetype = (useDocuAiEnabled && mimetype === mimeTypes.MIMETYPE_XML)
    ? mimeTypes.MIMETYPE_TEXT_XML
    : mimetype
  // FLAG:useDocuAi — video/audio get a real player (ported from the DMS admindashboard)
  // instead of falling through to the image branch, which produced an empty pane. flag-OFF
  // keeps today's behaviour (thumbnail, see showThumbnail below).
  //
  // The mimetype alone decides this — exactly as it does for the PDF branch — because the
  // drawer replaces its document object repeatedly while a fresh upload is analysed, and a
  // replacement without `minioId` used to flip this predicate and swap the player out and
  // back in. See `media-preview.ts`: a missing minioId is a loading state INSIDE the player,
  // never a reason to render a different viewer.
  const isPlayableMedia = isPlayableMediaPreview({ mimetype }, useDocuAiEnabled)

  const classes = useStyles({ ...props, isPdf, scrollable, isCarouselSlide, isPlayableMedia })

  // Zoom/rotate used to live ONLY in the drawer slice: the four buttons dispatch `updateDocument`,
  // which writes `state.document.drawer.document.metaData` — and nothing outside this file reads
  // `metaData.zoom` / `metaData.rotate`, so that slice is purely this component's own render input.
  // A host that feeds the preview through `props.document` (the invoice-artifact switcher in the
  // central review preview and in the Ausgaben drawer) renders a document the slice never holds, so
  // every click changed something nobody reads and the component did not even re-render — four
  // dead buttons.
  //
  // The rendered scale/rotation therefore comes from a local override, seeded from the document's
  // own metaData. The override is dropped again as soon as the INCOMING metaData moves, so wherever
  // the slice really is the source it stays authoritative and that path behaves exactly as before.
  const metaData = document?.metaData as { zoom?: number, rotate?: number } | undefined
  const metaZoom = metaData?.zoom
  const metaRotate = metaData?.rotate
  const [zoomOverride, setZoomOverride] = useState<number | null>(null)
  const [rotateOverride, setRotateOverride] = useState<number | null>(null)

  useEffect(() => { setZoomOverride(null) }, [document?.id, metaZoom])
  useEffect(() => { setRotateOverride(null) }, [document?.id, metaRotate])

  const zoom = zoomOverride ?? metaZoom ?? (isPdf ? ZOOM_DEFAULT_PDF : ZOOM_DEFAULT_IMAGE)
  const rotate = rotateOverride ?? metaRotate ?? 0

  // `updateDocument` only reaches the document rendered HERE when the drawer slice is what feeds it:
  // either directly (no `props.document`), or through a prop DERIVED from it — the Documents drawer
  // rebuilds `displayDocuments[0]` from the slice document (document-carousel-wrapper.tsx), so the
  // dispatch comes straight back as a new prop and survives the remount that switching artifacts
  // causes. For every other host the dispatch would only mutate an unrelated document, so it is
  // skipped there and the local override is the whole mechanism.
  const writesToDrawerSlice = !props.document
    || (!!drawerDocument && String(drawerDocument.id) === String(props.document.id))

  const applyZoom = (value: number): void => {
    setZoomOverride(value)
    if (writesToDrawerSlice) dispatch(updateDocument({ key: 'metaData.zoom', value }))
  }

  const applyRotate = (value: number): void => {
    setRotateOverride(value)
    if (writesToDrawerSlice) dispatch(updateDocument({ key: 'metaData.rotate', value }))
  }

  // FLAG:useDocuAi — the XML viewer and the KoSIT report frame accept neither `scale` nor `rotate`
  // (document-drawer-preview-xml.tsx, document-drawer-preview-html.tsx lay themselves out), so the
  // zoom/rotate buttons drawn beside them are exactly the dead controls this change removes
  // elsewhere. Gated on the flag, so flag-OFF keeps drawing whatever it draws today.
  const isValidationReportPreview = !!(useDocuAiEnabled && document?.isValidationReport)
  const rendersWithoutZoomAndRotate = !!useDocuAiEnabled
    && (isValidationReportPreview || effectiveMimetype === mimeTypes.MIMETYPE_TEXT_XML)

  const usesDedicatedViewer = isValidationReportPreview
    || effectiveMimetype === mimeTypes.MIMETYPE_PDF
    || effectiveMimetype === mimeTypes.MIMETYPE_TEXT_XML
    || isPlayableMedia

  // `thumbnailMimeTypes` (office) stays as an explicit floor so those types behave exactly as
  // before even if their mime arrives in an unexpected spelling; the second clause is the
  // general rule — anything reaching the `default:` branch that is not itself an image has no
  // viewer, so fall back to the DMS thumbnail rather than rendering an empty pane.
  // F16.1 — the widened rule is flag-ON only: dmsThumbnailUrl is set for dmsDirectUpload
  // tenants regardless of useDocuAi, so an ungated widening would flip flag-OFF slides
  // (e.g. generated application/xml) from the default-image branch into the thumbnail
  // branch (and hide zoom/rotate). Flag-OFF keeps the baseline triple verbatim.
  const showThumbnail = useDocuAiEnabled
    ? (!!dmsThumbnailUrl && !!document?.minioId && !usesDedicatedViewer
      && (thumbnailMimeTypes.includes(mimetype) || !isRenderableWithoutThumbnail(mimetype)))
    : !!(dmsThumbnailUrl && thumbnailMimeTypes.includes(mimetype) && document?.minioId)

  const catchError = async(error) => {
    setError(true)
    try {
      const responseData = error?.response?.data
      const parsed = typeof responseData?.text === 'function'
        ? JSON.parse(await responseData.text())
        : responseData
      if (parsed?.name === 'DmsError') {
        toast.showToast({ type: 'error', message: lang.dmsError })
      }
    } catch {
      // The preview already renders its error state; an unreadable response body must not mask it.
    }
  }

  useEffect(() => {
    let active = true
    setPageCount(0) // workaround for react-pdf issue #974
    setData(null)
    setError(false)

    // FLAG:useDocuAi — a video or audio file is streamed by the player itself, as a blob object
    // URL (document-drawer-preview-media.tsx). `data` is never read on that branch, so fetching
    // it here downloaded the ENTIRE media file a second time — and as a base64 `data:` string,
    // which is ~33% larger again and sits in React state for as long as the drawer is open.
    // `isPlayableMedia` is false whenever the flag is off, so flag-OFF still fetches exactly as
    // before.
    if (isPlayableMedia) {
      // Nothing to fetch: the player owns the bytes.
    } else {
      loadDocumentPreviewData({
        document,
        dmsFileUrl,
        httpHeaders: options.httpHeaders,
        isPdf,
        streamDmsFile: FileService.streamDmsFile,
      }).then(previewData => {
        if (active && previewData !== undefined) setData(previewData)
      }).catch(error => {
        if (active) catchError(error)
      })
    }
    // `isPlayableMedia` is a pure function of the mimetype and the flag; flag-OFF it is a
    // constant, so this dependency can never change there and the effect re-runs exactly as before.
    return () => { active = false }
  }, [document?.id, document?.url, isPlayableMedia])

  useEffect(() => {
    setCurrentPage(1)
    setPageHeight(null)
    setPageWindow(0)
    renderStartedAtRef.current = null
  }, [document?.id])

  // F16.4 — zoom/rotate change the rendered page size, and the frozen placeholder height would
  // desync the scroll geometry (placeholders keep the OLD height, the scrollbar jumps while
  // re-rastering). Reset ONLY the measured height so the next rendered page re-measures; the
  // current page deliberately stays where the user is.
  useEffect(() => {
    setPageHeight(null)
  }, [zoom, rotate])

  // FLAG:useDocuAi — remember how tall a rendered page actually is (so skipped pages reserve the
  // same space) and how long it took (so cheap documents get their neighbours prefetched again).
  // Passed to <Page> only on the flag-ON path.
  const handlePageRenderSuccess = useCallback((page: { height: number }) => {
    if (page?.height > 0) {
      setPageHeight(prev => (prev === null ? Math.round(page.height) : prev))
    }
    const startedAt = renderStartedAtRef.current
    if (startedAt !== null) {
      renderStartedAtRef.current = null
      if (performance.now() - startedAt < SLOW_PAGE_RENDER_MS) {
        setPageWindow(PAGE_WINDOW_FAST)
      }
    }
  }, [])

  // Merge PDFs when signature toggle is ON
  useEffect(() => {
    // Cleanup previous merged data
    if (mergedPdfData) {
      URL.revokeObjectURL(mergedPdfData)
      setMergedPdfData(null)
    }

    // Only merge if toggle is ON and we have both PDFs loaded
    if (showSignature && signatureDocument && isPdf && data) {
      setIsMerging(true)

      mergePdfDocuments(
        data,
        signatureDocument,
        { httpHeaders: options.httpHeaders, dmsFileUrl },
      )
        .then(mergedBlob => {
          const url = URL.createObjectURL(mergedBlob)
          setMergedPdfData(url)
        })
        .catch(error => {
          console.error('PDF merge failed:', error)
          toast.showToast({
            type: 'error',
            message: 'Fehler beim Zusammenführen der Dokumente',
          })
          if (props.onToggleSignature) {
            props.onToggleSignature(false) // Turn toggle off on error
          }
        })
        .finally(() => setIsMerging(false))
    }
  }, [showSignature, data, signatureDocument?.minioId, signatureDocument?.url])

  // Cleanup merged data on unmount
  useEffect(() => {
    return () => {
      if (mergedPdfData) URL.revokeObjectURL(mergedPdfData)
    }
  }, [mergedPdfData])

  let downloadUrl = `${document?.url}?download=true&token=${user.token}`
  if (collaborationId) {
    downloadUrl = `${downloadUrl}&collaborationId=${collaborationId}`
  }

  // Download handler - downloads current view (original or merged)
  const handleDownload = () => {
    const displayData = mergedPdfData || data
    const fileName = document?.decodedName || document?.name

    if (displayData && isPdf) {
      // Download from current blob using anchor element
      const finalFileName = showSignature
        ? fileName.replace('.pdf', '_mit_unterschrift.pdf')
        : fileName

      // Create temporary anchor element for download
      const link = window.document.createElement('a')
      link.href = displayData
      link.download = finalFileName
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
    } else if (data && !document?.url) {
      const link = window.document.createElement('a')
      link.href = displayData
      link.download = fileName
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
    } else {
      // Fallback for non-PDF or if no blob available
      window.open(downloadUrl, '_blank')
    }
  }

  const options = useMemo(() => {
    return { httpHeaders: { authorization: `Bearer ${user.token}`, ...collaborationHeaders } }
  }, [user.token, collaborationHeaders])

  const onLoadSuccess = (pdf): void => {
    // FLAG:useDocuAi — start the clock for the first page rasterisation (see handlePageRenderSuccess).
    renderStartedAtRef.current = performance.now()
    setPdf(pdf)
    setPageCount(pdf.numPages)

    // Force update once content is definitely in the DOM (a short timeout can help)
    setTimeout(() => {
      scrollSpy.update()
      scroll.scrollToTop({
        containerId: 'containerElement',
        duration: 0,
        smooth: false,
      })
    }, 0)
  }

  if (!document) {
    return null
  }

  const handleThumbnailClick = ({ pageNumber }: { pageNumber: number }): void => {
    setCurrentPage(pageNumber)
  }

  const pdfZoomProps = {
    wheel: { disabled: true },
    pinch: { disabled: true },
    doubleClick: { disabled: true },
    maxScale: 3,
    minScale: 1,
  }

  const renderPages = useMemo(() => {
    if (!pdf || !isPdf) return () => null
    return () =>
      times(pageCount, (i) => {
        const pageNumber = i + 1
        // FLAG:useDocuAi — only rasterise pages near the one being looked at; the rest reserve
        // their space. `!useDocuAiEnabled` short-circuits to true, so flag-OFF renders every page
        // exactly as before and never reaches the placeholder branch.
        const isInWindow = !useDocuAiEnabled || Math.abs(pageNumber - currentPage) <= pageWindow
        if (!isInWindow) {
          return (
            <Element key={i} id={`page-${pageNumber}`} name={`page-${pageNumber}`}>
              <div
                className={classes.pagePlaceholder}
                style={{ height: pageHeight ?? PAGE_PLACEHOLDER_HEIGHT }}
              />
            </Element>
          )
        }
        return (
          <Element key={i} id={`page-${pageNumber}`} name={`page-${pageNumber}`}>
            <TransformWrapper key={i} {...pdfZoomProps}>
              <TransformComponent>
                <Page
                  className={clsx([classes.page, `page-${pageNumber}`])}
                  pageNumber={pageNumber}
                  scale={zoom}
                  rotate={rotate}
                  {...(useDocuAiEnabled ? { onRenderSuccess: handlePageRenderSuccess } : {})}
                />
              </TransformComponent>
            </TransformWrapper>
          </Element>
        )
      })
  }, [pdf, isPdf, pageCount, zoom, rotate, useDocuAiEnabled, currentPage, pageWindow, pageHeight, handlePageRenderSuccess])

  const thumbnails = useMemo(() => {
    if (!pdf || !isPdf || (dmsFileUrl && !data)) { return null }
    return times(pageCount, (i) => {
      const pageNumber = i + 1
      return (
        <Link
          key={pageNumber}
          onSetActive={() => setCurrentPage(pageNumber)}
          spy={true}
          containerId="containerElement"
          to={`page-${pageNumber}`}
          smooth={true}
          offset={-40}
          duration={500}>
          <div key={pageNumber} className={classes.thumbnailWrapper} >
            {pdf && pageNumber <= pageCount && (
              <div
                onClick={() => handleThumbnailClick({ pageNumber })}
                className={classes.thumbnail}
              >
                {/* FLAG:useDocuAi — a thumbnail rasterises the full page (and pays the same image
                    decode as the page itself), so window them too. flag-OFF keeps rendering all. */}
                {(!useDocuAiEnabled || Math.abs(pageNumber - currentPage) <= (pageWindow === 0 ? 0 : THUMBNAIL_WINDOW_FAST))
                  ? <Thumbnail
                    pdf={pdf}
                    pageNumber={pageNumber}
                    height={195}
                    width={122}
                  />
                  : <div className={classes.thumbnailPlaceholder} />}
              </div>
            )}
            <span className={classes.thumbnailLabel}>{`Seite ${pageNumber}`}</span>
          </div>
        </Link>
      )
    })
  }, [pdf, isPdf, document?.id, currentPage, data, dmsFileUrl, useDocuAiEnabled, pageWindow])

  const getMimeTypeComponent = () => {
    // FLAG:useDocuAi — the KoSIT validation-report artifact embeds the human-readable report as an
    // <html> block inside its SVRL XML envelope; render that HTML (sliced in DocumentDrawerPreviewHtml)
    // instead of the raw XML. flag-OFF never sets isValidationReport, so this branch is never entered.
    if (useDocuAiEnabled && document.isValidationReport) {
      // Pass the stream state along: without it the preview cannot distinguish "still fetching"
      // from "fetch failed" / "report carries no HTML", and every one of those looked like an
      // endless loading state to the user.
      return <DocumentDrawerPreviewHtml data={data} isLoading={!data && !error} hasError={error} />
    }
    // FLAG:useDocuAi — DMS invoice artifacts (extracted XML, KoSIT validation report) come as
    // application/xml; render them as XML instead of the default placeholder. flag-OFF keeps the raw
    // mimetype (→ default), so the response/behaviour is unchanged for non-useDocuAi tenants.
    // `effectiveMimetype` is hoisted to the component body (it also decides `showThumbnail`).
    // FLAG:useDocuAi — media before the switch: it keys on the video/ + audio/ PREFIX, which a
    // switch over exact mimetypes cannot express (video/mp4, video/quicktime, audio/mpeg, …).
    if (isPlayableMedia) {
      return (
        <Element
          className={clsx(classes.image, isCarouselSlide && classes.carouselImage)}
          id="containerElement"
        >
          <DocumentDrawerPreviewMedia document={document} />
        </Element>
      )
    }
    switch (effectiveMimetype) {
      case mimeTypes.MIMETYPE_PDF: {
        const displayData = mergedPdfData || data
        return (
          <>
            <Element
              className={clsx(classes.document, isCarouselSlide && classes.carouselDocument)}
              id="containerElement"
            >
              <div>
                {isMerging && <Loader text="Dokumente werden zusammengeführt..." />}
                {!isMerging && ((dmsFileUrl && document.minioId && !document.isGeneratedFile && !displayData && !error) || (!displayData && !error && document.url && !document.data))
                  ? <Loader text={lang.documents.loadingFile} />
                  : !isMerging && <Document file={displayData ?? document.data} renderMode='canvas'
                    loading={lang.pdfLoading}
                    error={lang.pdfLoadingFailed}
                    noData={error ? lang.pdfLoadingFailed : lang.pdfLoading}
                    options={options}
                    onLoadSuccess={onLoadSuccess}
                  >
                    {renderPages()}
                  </Document>}
              </div>
            </Element>
            {currentPage !== 1 && (
              <DocumentControlButton
                customClass={classes.backToTopButton}
                icon={IconArrowUp}
                title={lang.expense.previewToolbar.items.backToTop}
                onClick={() => {
                  setCurrentPage(1)
                  scroll.scrollToTop({
                    duration: 800,
                    smooth: 'easeInOutQuart',
                    offset: -40,
                    containerId: 'containerElement',
                  })
                }}
              />
            )}
          </>
        )
      }
      case mimeTypes.MIMETYPE_TEXT_XML: {
        return (
          <DocumentDrawerPreviewXml
            receiptUrl={document.url}
            name={document.name}
            data={data}
            minioId={document.minioId}
          />
        )
      }
      default: {
        return (
          <Element
            className={clsx(classes.image, isCarouselSlide && classes.carouselImage)}
            id="containerElement"
          >
            <DocumentDrawerPreviewImage
              document={document}
              showThumbnail={!!showThumbnail}
              scale={zoom}
              rotate={rotate}
              width={document.metaData?.width}
              height={document.metaData?.height}
            />
          </Element>
        )
      }
    }
  }

  return (
    <div
      className={clsx(classes.root, isCarouselSlide && classes.carouselRoot)}
    >
      <div className={classes.title}>
        {/* FLAG:useDocuAi — truncate without losing the extension (see titleNameWithExtension). With
            flag-OFF it remains the unchanged h1 containing the full name.     */}
        {useDocuAiEnabled
          ? (
            <h1 className={classes.titleNameWithExtension} title={displayFileName}>
              <span className={classes.titleNameStem}>{displayFileNameParts.stem}</span>
              {!!displayFileNameParts.extension && (
                <span className={classes.titleNameExtension}>{displayFileNameParts.extension}</span>
              )}
            </h1>
          )
          : (
            <h1>
              {document?.decodedName ?? customDecodeUri(document?.name)}
            </h1>
          )}

        {/* FLAG:useDocuAi — valid/invalid hint for DocuAI-analysed e-invoices.
            document.validationResult is only ever populated (serialize.js) for useDocuAi tenants, but
            we still gate on useDocuAiEnabled explicitly so legacy/development never renders this even
            if validationResult happens to be present (outgoing-invoice xmlValidationResult path). */}
        {useDocuAiEnabled && document?.validationResult && !hideValidityBadge && (
          <div className={classes.validityHint}>
            <SVGIcon
              svg={document.validationResult.valid ? ValidIcon : InvalidIcon}
              height="18px"
              width="18px"
              fill={document.validationResult.valid ? theme.green : theme.error}
            />
            <span>
              {document.validationResult.valid ? lang.receipt.analyseResult.valid : lang.receipt.analyseResult.invalid}
            </span>
          </div>
        )}

        <StateFields
          document={document}
          shouldRender={!isSignatureFile}
        />
      </div>
      <div
        className={clsx(classes.leftArea, isCarouselSlide && classes.carouselLeftArea)}
      >
        <div className={classes.actions}>
          {/* FLAG:useDocuAi — zoom/rotate are meaningless for a video or audio file (the transport
              bar is its control surface) and for the XML / KoSIT-report artifacts, which take
              neither scale nor rotate — see `rendersWithoutZoomAndRotate`.
              The enabled/disabled state and the values written now come from the EFFECTIVE zoom and
              rotation, not from `metaData` alone: `metaData.zoom` is frequently absent, and
              `undefined ± step` is NaN — which made every comparison false (button always enabled)
              and turned the rotate click into `rotate: NaN`. */}
          {!showThumbnail && !isPlayableMedia && !rendersWithoutZoomAndRotate && <>
            <DocumentControlButton
              disabled={zoom + ZOOM_STEP > ZOOM_MAX}
              icon={IconZoomPlus}
              debounceTime={0.1}
              title={lang.expense.previewToolbar.items.zoomIn + ` (${Math.min(Math.round(zoom * 100)).toFixed(0)}%)`}
              onClick={() => applyZoom(Math.min(zoom + ZOOM_STEP, ZOOM_MAX))}
            />
            <DocumentControlButton
              icon={IconZoomMinus}
              disabled={zoom - ZOOM_STEP < ZOOM_MIN}
              debounceTime={0.1}
              title={lang.expense.previewToolbar.items.zoomOut + ` (${Math.min(Math.round(zoom * 100)).toFixed(0)}%)`}
              onClick={() => applyZoom(Math.max(zoom - ZOOM_STEP, ZOOM_MIN))}
            />
            <DocumentControlButton
              icon={IconRotate}
              debounceTime={0.1}
              title={lang.expense.previewToolbar.items.rotate90}
              onClick={() => applyRotate(rotate >= 270 ? 0 : rotate + 90)}
            />
            <DocumentControlButton
              icon={IconReset}
              debounceTime={0.1}
              title={lang.expense.previewToolbar.items.reset}
              onClick={() => {
                applyRotate(0)
                applyZoom(1)
              }}
            />
          </>}
          <DocumentControlButton
            icon={IconDownload}
            title={lang.expense.previewToolbar.items.download}
            onClick={handleDownload}
          />
          {toolbarActions}
        </div>
        {isPdf && (
          <div className={clsx(classes.thumbnails, isCarouselSlide && classes.carouselThumbnails)}>
            {!pdf || !isPdf || (dmsFileUrl && !data)
              ? null
              : <Pager
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pageCount={pageCount}
              />}
            {thumbnails ? <PerfectScrollbar className={isCarouselSlide ? classes.carouselScrollbar : ''}>{thumbnails}</PerfectScrollbar> : null}
          </div>
        )}
      </div>

      <div className={clsx(classes.rightArea, isCarouselSlide && classes.carouselRightArea)}>
        <>
          {getMimeTypeComponent()}
        </>
      </div>
    </div>
  )
}

export default DocumentDrawerPreview
